import { useFormikContext } from 'formik'
import React from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import {
  DEADLINE_MISSED_MESSAGE,
  isDeadlinePassed,
  scoreError,
} from 'src/views/pages/Authenticated/administrator/courses/details/AdministratorCourseScoresPage/validation'

import { Form } from 'src/views/components/forms/formik/Form'
import ScoreField from 'src/views/components/forms/ScoreField'

interface ScoresFormProps {
  readonly student: Api.LecturerUser
  readonly criteria: Api.PaperCriteria
  readonly setFinalScore: React.Dispatch<React.SetStateAction<{ [key: string]: unknown }>>
  readonly groupsPaper: Api.Paper | null
}

export default function ScoresForm({
  student,
  setFinalScore,
  criteria,
  groupsPaper,
}: ScoresFormProps): React.ReactElement {
  const score = student.scores?.find((score) => score.criteriaId.toString() === criteria.id)?.score
  const [submitedScore, setSubmitedScore] = React.useState(score)
  const headers = useAuthenticatedHeaders()
  const t = useTranslatable()
  const [searchParams] = useSearchParams()
  const { courseId } = useParams()
  const [submitedSuccessfully, setSubmitedSuccessfully] = React.useState(false)

  const groupId = searchParams.get('groupId')

  async function onSubmit(values: { score: number | string | null | undefined }): Promise<void> {
    setSubmitedScore(values.score == null ? values.score : Number(values.score))

    const rangeError = scoreError(values.score, criteria.max, t)
    if (rangeError != null) {
      throw { type: 'ErrorsObject', errors: { score: [rangeError] } }
    }

    const result = await Api.postStudentsScore({
      headers,
      args: {
        courseId: courseId!,
        groupId: student.group?.id ?? groupId!,
        profileId: student.profileId.toString(),
      },
      body: {
        criteriaId: criteria.id,
        // ScoreField keeps the typed text; the API expects a number.
        score: Number(values.score ?? 0),
      },
    }).catch((error: base.CommonError) => {
      if (error.type === 'ErrorMessage' && error.message === DEADLINE_MISSED_MESSAGE) {
        throw { type: 'ErrorMessage', message: t('course:score_deadline_missed') }
      }
      throw error
    })
    setFinalScore((prev) => ({ ...prev, [student.profileId.toString()]: result }))
    setSubmitedSuccessfully(true)
  }

  return (
    <Form onSubmit={onSubmit} initialValues={{ score }} isConfirmable={!submitedSuccessfully}>
      <FormContent criteria={criteria} student={student} submitedScore={submitedScore} groupsPaper={groupsPaper} />
    </Form>
  )
}
interface FormContentProps {
  readonly criteria: Api.PaperCriteria
  readonly student: Api.LecturerUser
  readonly submitedScore: number | null | undefined
  readonly groupsPaper: Api.Paper | null
}

function FormContent({ criteria, student, submitedScore, groupsPaper }: FormContentProps): React.ReactElement {
  const formik = useFormikContext<{ score: string }>()
  // The API keeps `canEdit` true after the deadline and only rejects the save, so lock the cell here too.
  const disabled =
    student.group?.paper?.active === false ||
    groupsPaper?.active === false ||
    !criteria.canEdit ||
    isDeadlinePassed(criteria.deadline)

  return (
    <ScoreField
      name="score"
      loading={formik.isSubmitting}
      disabled={disabled}
      submit={formik.handleSubmit}
      submitedScore={submitedScore}
    />
  )
}
